import type { ApplicationConfig } from '@angular/core';
import {
  APP_INITIALIZER,
  ENVIRONMENT_INITIALIZER,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideZonelessChangeDetection,
} from '@angular/core';
import { EVENT_MANAGER_PLUGINS } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withNavigationErrorHandler,
  withRouterConfig,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import { provideHttpClient } from '@angular/common/http';
import { error } from '@helpers/engine/logging';

import { provideNgIconsConfig } from '@ng-icons/core';
import { NgSelectConfig } from '@ng-select/ng-select';
import {
  popperVariation,
  provideTippyConfig,
  provideTippyLoader,
  tooltipVariation,
  withContextMenuVariation,
} from '@ngneat/helipopper/config';
import { routes } from '@routes/app.routes';
import { AnalyticsService } from '@services/analytics.service';
import { APIService } from '@services/api.service';
import { BGMService } from '@services/bgm.service';
import { ClickModifiersPlugin } from '@services/click-modifiers.plugin';
import { ContentService } from '@services/content.service';
import { GamestateService } from '@services/gamestate.service';
import { LoggerService, RollbarErrorHandler } from '@services/logger.service';
import { MetaService } from '@services/meta.service';
import { NotifyService } from '@services/notify.service';
import { TaskService } from '@services/task.service';
import { SoundService } from '@services/sound.service';
import { ThemeService } from '@services/theme.service';
import { AdventureLogTimeagoFormatter } from '@services/timeago-formatter.service';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { NG_EVENT_PLUGINS } from '@taiga-ui/event-plugins';
import { TimeagoFormatter, provideTimeago } from 'ngx-timeago';
import { provideToastr } from 'ngx-toastr';

export const appConfig: ApplicationConfig = {
  providers: [
    NG_EVENT_PLUGINS,
    {
      provide: EVENT_MANAGER_PLUGINS,
      useClass: ClickModifiersPlugin,
      multi: true,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideHttpClient(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withNavigationErrorHandler((err) => {
        error('Router', 'Navigation error:', err);
        return '/';
      }),
    ),
    provideNgIconsConfig({
      size: '1.5em',
    }),
    importProvidersFrom(
      SweetAlert2Module.forRoot({
        provideSwal: () =>
          import('sweetalert2/dist/sweetalert2.js').then(({ default: swal }) =>
            swal.mixin({
              theme: 'dark',
              reverseButtons: true,
              showCancelButton: true,
              focusCancel: true,
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary',
                denyButton: 'btn btn-error',
                cancelButton: 'btn btn-neutral btn-outline',
              },
            }),
          ),
      }),
    ),
    provideAnimations(),
    provideTimeago({
      formatter: {
        provide: TimeagoFormatter,
        useClass: AdventureLogTimeagoFormatter,
      },
    }),
    provideToastr({
      positionClass: 'toast-bottom-right',
      timeOut: 3000,
      autoDismiss: true,
      closeButton: true,
      maxOpened: 1,
    }),
    provideTippyLoader(() => import('tippy.js')),
    provideTippyConfig({
      defaultVariation: 'tooltip',
      variations: {
        tooltip: tooltipVariation,
        popper: popperVariation,
        contextMenu: {
          ...withContextMenuVariation(popperVariation),
          theme: 'translucent',
        },
        itemComparison: { ...tooltipVariation },
      },
    }),
    {
      provide: ErrorHandler,
      useClass: RollbarErrorHandler,
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useValue: async () => {
        const meta = inject(MetaService);
        const logger = inject(LoggerService);
        const analytics = inject(AnalyticsService);

        void meta.init();
        logger.init();
        analytics.init();
      },
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(APIService).init(),
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(NotifyService).init(),
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(TaskService).init(),
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: async () => await inject(ContentService).init(),
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(GamestateService).init(),
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(ThemeService).init(),
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: async () => {
        await inject(SoundService).init();
      },
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(BGMService).init(),
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => {
        inject(NgSelectConfig).appendTo = 'body';
      },
    },
  ],
};
